import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Rocket,
  AlertTriangle,
  Calendar,
  XCircle,
  FileCheck,
  Users,
  FolderKanban,
  Clock,
  ChevronRight,
  TrendingUp,
  BarChart3
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { useNavigate } from 'react-router-dom';

const MetricCard = ({ icon: Icon, label, value, bgColor, iconColor, trend, onClick }) => (
  <div 
    className={`metric-card group ${onClick ? 'cursor-pointer hover:shadow-lg transition-shadow' : ''}`} 
    data-testid={`metric-${label.toLowerCase().replace(/\s+/g, '-')}`}
    onClick={onClick}
  >
    <div className="flex items-start justify-between">
      <div className={`metric-icon ${bgColor}`}>
        <Icon className={`w-5 h-5 ${iconColor}`} strokeWidth={1.5} />
      </div>
      {trend && (
        <div className="flex items-center gap-1 text-xs text-emerald-600">
          <TrendingUp className="w-3 h-3" />
          <span>{trend}</span>
        </div>
      )}
      {onClick && (
        <ChevronRight className="w-4 h-4 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
      )}
    </div>
    <div>
      <p className="metric-label">{label}</p>
      <p className="metric-value">{value}</p>
    </div>
  </div>
);

const COLORS = ['#2563eb', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

export const DashboardPage = () => {
  const { api, user } = useAuth();
  const { subscribe, isConnected } = useWebSocket();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [myStats, setMyStats] = useState(null);
  const [contentByStatus, setContentByStatus] = useState({});
  const [projectsByStatus, setProjectsByStatus] = useState({});
  const [tasksByAssignee, setTasksByAssignee] = useState([]);
  const [upcomingContent, setUpcomingContent] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const refreshTimeoutRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      // Use combined endpoint for better performance (single request instead of 7)
      const response = await api.get('/dashboard/all');
      const data = response.data;

      setStats(data.stats);
      setMyStats(data.my_stats);
      setContentByStatus(data.content_by_status);
      setProjectsByStatus(data.projects_by_status);
      setTasksByAssignee(data.tasks_by_assignee);
      setUpcomingContent(data.upcoming_content);
      setUpcomingEvents(data.upcoming_events);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Real-time updates with debouncing for dashboard
  useEffect(() => {
    const debouncedRefresh = () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
      refreshTimeoutRef.current = setTimeout(() => {
        fetchData();
      }, 500);
    };

    const unsubscribes = [];
    const entityTypes = ['project', 'task', 'content', 'event', 'asset', 'approval'];
    const actions = ['created', 'updated', 'deleted'];

    entityTypes.forEach(entity => {
      actions.forEach(action => {
        unsubscribes.push(subscribe(`${entity}_${action}`, debouncedRefresh));
      });
    });

    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
      unsubscribes.forEach(unsub => unsub());
    };
  }, [subscribe, fetchData]);

  const contentStatusData = Object.entries(contentByStatus).map(([status, count]) => ({
    name: status.replace('_', ' '),
    value: count
  }));

  const projectsStatusData = Object.entries(projectsByStatus).map(([status, count]) => ({
    name: status.replace('_', ' '),
    value: count
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 md:space-y-8 animate-fade-in" data-testid="dashboard-page">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">Executive Overview</h1>
          <p className="text-sm sm:text-base text-slate-500 mt-1">Welcome back, {user?.name}</p>
        </div>
        <Button variant="outline" className="gap-2 w-full sm:w-auto" data-testid="view-reports-btn">
          <BarChart3 className="w-4 h-4" />
          View Reports
        </Button>
      </div>

      {/* View Tabs */}
      <Tabs defaultValue="executive" className="space-y-6">
        <TabsList className="bg-slate-100 w-full sm:w-auto">
          <TabsTrigger value="executive" className="flex-1 sm:flex-none" data-testid="tab-executive">Executive View</TabsTrigger>
          <TabsTrigger value="team" className="flex-1 sm:flex-none" data-testid="tab-team">Team View</TabsTrigger>
        </TabsList>

        {/* Executive View */}
        <TabsContent value="executive" className="space-y-6">
          {/* KPI Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
            <MetricCard
              icon={Rocket}
              label="Launches This Week"
              value={stats?.launches_this_week || 0}
              bgColor="bg-blue-50"
              iconColor="text-blue-600"
              onClick={() => navigate('/content')}
            />
            <MetricCard
              icon={AlertTriangle}
              label="Overdue Tasks"
              value={stats?.overdue_tasks || 0}
              bgColor="bg-amber-50"
              iconColor="text-amber-600"
              onClick={() => navigate('/tasks?filter=overdue')}
            />
            <MetricCard
              icon={Calendar}
              label="Events This Week"
              value={stats?.events_this_week || 0}
              bgColor="bg-emerald-50"
              iconColor="text-emerald-600"
              onClick={() => navigate('/events')}
            />
            <MetricCard
              icon={XCircle}
              label="Blocked Tasks"
              value={stats?.blocked_tasks || 0}
              bgColor="bg-red-50"
              iconColor="text-red-600"
              onClick={() => navigate('/tasks?filter=blocked')}
            />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
            <MetricCard
              icon={FileCheck}
              label="Pending Approvals"
              value={stats?.pending_approvals || 0}
              bgColor="bg-purple-50"
              iconColor="text-purple-600"
              onClick={() => navigate('/assets?tab=pending')}
            />
            <MetricCard
              icon={Clock}
              label="Content in Review"
              value={stats?.content_in_review || 0}
              bgColor="bg-cyan-50"
              iconColor="text-cyan-600"
              onClick={() => navigate('/calendar?status=in_review')}
            />
            <MetricCard
              icon={FolderKanban}
              label="Active Projects"
              value={stats?.active_projects || 0}
              bgColor="bg-indigo-50"
              iconColor="text-indigo-600"
              onClick={() => navigate('/projects?status=active')}
            />
            <MetricCard
              icon={Users}
              label="Team Capacity"
              value={`${stats?.team_capacity || 0}%`}
              bgColor="bg-pink-50"
              iconColor="text-pink-600"
              onClick={() => navigate('/tasks')}
            />
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Content Pipeline */}
            <Card className="mcc-card lg:col-span-1">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold text-slate-900">Content Pipeline</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64 min-h-[256px]">
                  <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
                    <PieChart>
                      <Pie
                        data={contentStatusData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={4}
                        dataKey="value"
                      >
                        {contentStatusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-4">
                  {contentStatusData.map((item, index) => (
                    <div key={item.name} className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      />
                      <span className="text-xs text-slate-600 capitalize">{item.name}</span>
                      <span className="text-xs font-semibold text-slate-900 ml-auto">{item.value}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Team Workload */}
            <Card className="mcc-card lg:col-span-2">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold text-slate-900">Team Workload</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64 min-h-[256px]">
                  <ResponsiveContainer width="100%" height="100%" minWidth={300} minHeight={200}>
                    <BarChart data={tasksByAssignee} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis type="number" tick={{ fontSize: 12 }} />
                      <YAxis 
                        dataKey="name" 
                        type="category" 
                        width={100}
                        tick={{ fontSize: 12 }}
                      />
                      <Tooltip />
                      <Bar dataKey="count" fill="#2563eb" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Upcoming Items */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Upcoming Content */}
            <Card className="mcc-card">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-base font-semibold text-slate-900">Upcoming Launches</CardTitle>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-blue-600"
                  onClick={() => navigate('/calendar')}
                  data-testid="view-calendar-btn"
                >
                  View Calendar
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-64">
                  {upcomingContent.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-8">No upcoming launches</p>
                  ) : (
                    <div className="space-y-3">
                      {upcomingContent.map((content) => (
                        <div 
                          key={content.id}
                          className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer"
                          onClick={() => navigate(`/calendar`)}
                          data-testid={`content-item-${content.id}`}
                        >
                          <div className={`w-1 h-10 rounded-full bg-blue-500`} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-900 truncate">{content.title}</p>
                            <p className="text-xs text-slate-500">{content.content_type} • {content.channels?.join(', ')}</p>
                          </div>
                          <div className="text-right">
                            <Badge variant="secondary" className="text-xs">
                              {content.publish_date ? format(parseISO(content.publish_date), 'MMM d') : 'TBD'}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Upcoming Events */}
            <Card className="mcc-card">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-base font-semibold text-slate-900">Upcoming Events</CardTitle>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-blue-600"
                  onClick={() => navigate('/events')}
                  data-testid="view-events-btn"
                >
                  View All
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-64">
                  {upcomingEvents.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-8">No upcoming events</p>
                  ) : (
                    <div className="space-y-3">
                      {upcomingEvents.map((event) => (
                        <div 
                          key={event.id}
                          className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer"
                          onClick={() => navigate(`/events`)}
                          data-testid={`event-item-${event.id}`}
                        >
                          <div className="w-1 h-10 rounded-full bg-emerald-500" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-900 truncate">{event.name}</p>
                            <p className="text-xs text-slate-500">{event.event_type} • {event.location || 'Virtual'}</p>
                          </div>
                          <div className="text-right">
                            <Badge variant="secondary" className="text-xs">
                              {event.start_date ? format(parseISO(event.start_date), 'MMM d') : 'TBD'}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Team View */}
        <TabsContent value="team" className="space-y-6">
          {/* Team Workload Distribution */}
          <Card className="mcc-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold text-slate-900">Team Workload Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-72 min-h-[288px]">
                <ResponsiveContainer width="100%" height="100%" minWidth={300} minHeight={250}>
                  <BarChart data={tasksByAssignee} layout="vertical" margin={{ left: 80, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                    <XAxis type="number" />
                    <YAxis 
                      type="category" 
                      dataKey="name" 
                      tick={{ fontSize: 12 }}
                      width={75}
                    />
                    <Tooltip />
                    <Bar dataKey="count" fill="#3b82f6" radius={[0, 4, 4, 0]} name="Assigned Tasks" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Team Member Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {tasksByAssignee.map((member, index) => (
              <Card 
                key={member.name || index} 
                className="mcc-card hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/tasks?assigned_to=${member.user_id}`)}
              >
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                      <span className="text-blue-700 font-semibold">
                        {member.name?.split(' ').map(n => n[0]).join('').slice(0, 2) || '??'}
                      </span>
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">{member.name || 'Unassigned'}</p>
                      <p className="text-sm text-slate-500">{member.count} active task{member.count !== 1 ? 's' : ''}</p>
                    </div>
                    <div className="text-right">
                      <div className={`text-2xl font-bold ${
                        member.count > 5 ? 'text-red-600' : 
                        member.count > 3 ? 'text-amber-600' : 'text-emerald-600'
                      }`}>
                        {member.count > 5 ? 'High' : member.count > 3 ? 'Medium' : 'Low'}
                      </div>
                      <p className="text-xs text-slate-400">workload</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* My Personal Stats */}
          <Card className="mcc-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold text-slate-900">My Personal Stats</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <p className="text-2xl font-bold text-blue-700">{myStats?.my_tasks || 0}</p>
                  <p className="text-xs text-slate-600">Tasks Assigned</p>
                </div>
                <div className="text-center p-4 bg-amber-50 rounded-lg">
                  <p className="text-2xl font-bold text-amber-700">{myStats?.my_overdue || 0}</p>
                  <p className="text-xs text-slate-600">Overdue</p>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <p className="text-2xl font-bold text-purple-700">{myStats?.my_approvals || 0}</p>
                  <p className="text-xs text-slate-600">Pending Approvals</p>
                </div>
                <div className="text-center p-4 bg-cyan-50 rounded-lg">
                  <p className="text-2xl font-bold text-cyan-700">{myStats?.my_content || 0}</p>
                  <p className="text-xs text-slate-600">Content Items</p>
                </div>
                <div className="text-center p-4 bg-emerald-50 rounded-lg">
                  <p className="text-2xl font-bold text-emerald-700">{myStats?.my_events || 0}</p>
                  <p className="text-xs text-slate-600">Events</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

const CheckSquare = ({ className, strokeWidth }) => (
  <svg className={className} strokeWidth={strokeWidth} viewBox="0 0 24 24" fill="none" stroke="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M9 11l3 3L22 4" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
